import { Button, Center, Overlay, Text } from "@mantine/core";
import { type FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CheckmarkBounce } from "@/common-ui/animations/checkmark-bounce";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import type { TranscriptContent } from "../api/evaluations/types";
import EvaluationCallout from "./EvaluationCallout";
import classes from "./EvaluationPage.module.css";
import EvaluationTranscript from "./EvaluationTranscript";
import SalesBehaviorsChecklist from "./SalesBehaviorsChecklist";

export interface TrainingEvaluationPageProps {
    onCompleteEvaluation: () => void;
    evaluationResults: TranscriptContent;
}

export const TrainingEvaluationPage: FC<TrainingEvaluationPageProps> = ({
    onCompleteEvaluation,
    evaluationResults,
}) => {
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const [activeBehaviorIndex, setActiveBehaviorIndex] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [completeEvaluation, setCompleteEvaluation] = useState(false);
    const behaviors = useMemo(() => evaluationResults?.EvaluationDetails?.Body ?? [], [evaluationResults]);

    useEffect(() => {
        if (completeEvaluation) {
            onCompleteEvaluation();
        }
    }, [completeEvaluation, onCompleteEvaluation]);

    useLayoutEffect(() => {
        if (transcriptContainerRef?.current) {
            const offset = transcriptContainerRef.current.scrollHeight / 2 - 100;
            setScrollOffset(offset);
        }
    }, []);

    return (
        <div className={classes.container}>
            {overlayVisible && (
                <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                    <Center
                        style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
                        className={sharedStyles.animateScaleIn}
                    >
                        <CheckmarkBounce />
                        <Text fw="700" ta="center" size="xl">
                            Thank you for reviewing!
                        </Text>
                        <Button
                            onClick={() => {
                                setCompleteEvaluation(true);
                            }}
                        >
                            Return Home
                        </Button>
                    </Center>
                </Overlay>
            )}
            <div className={classes.evaluationGrid}>
                <div className={classes.evaluationGridColumn}>
                    <SalesBehaviorsChecklist behaviors={behaviors} />
                </div>
                <div className={classes.evaluationGridColumn} ref={transcriptContainerRef}>
                    <EvaluationTranscript
                        activeBehavior={behaviors[activeBehaviorIndex]?.behavior}
                        evaluationResults={evaluationResults}
                        onClickHighlight={(transcriptBehavior) => {
                            if (transcriptBehavior) {
                                const newBehaviorIndex = behaviors.findIndex(
                                    ({ behavior }) => behavior === transcriptBehavior,
                                );
                                const index = newBehaviorIndex >= 0 ? newBehaviorIndex : 0;
                                setActiveBehaviorIndex(index);
                            } else {
                                setActiveBehaviorIndex(0);
                            }
                        }}
                        scrollOffset={scrollOffset}
                    />
                </div>
                <div className={classes.evaluationGridColumn}>
                    <EvaluationCallout
                        key={behaviors[activeBehaviorIndex]?.behavior}
                        activeBehavior={behaviors[activeBehaviorIndex]}
                        onClickOk={() => {
                            const nextBehavior = activeBehaviorIndex + 1;

                            if (nextBehavior >= behaviors.length) {
                                setOverlayVisible(true);
                            } else {
                                setActiveBehaviorIndex(nextBehavior);
                            }
                        }}
                        pagination={
                            behaviors.length > 0 ? (
                                <Text size="xxs">
                                    {activeBehaviorIndex + 1} of {behaviors.length}
                                </Text>
                            ) : null
                        }
                        scrollOffset={scrollOffset}
                    />
                </div>
            </div>
        </div>
    );
};

export default TrainingEvaluationPage;
